import { Box, Stack, Typography } from '@mui/material'
import { C1Icon } from 'components/core/icons/C1Icon'
import { EmergencyAccessTaskIcon } from 'components/core/task/task-type/EmergencyAccessTaskComponent'
import { RelativeTimestamp } from 'components/core/time/display'
import { C1Title } from 'components/no-ui/C1Title'
import { getTaskTitleIntl } from 'components/pages/task/details/content/getTaskTitleIntl'
import { TaskStatusLabel } from 'components/pages/task/details/content/TaskStatusLabel'
import { TaskTitleFormattedMessage } from 'components/pages/task/details/content/TaskTitleFormattedMessage'
import { TaskActionOptions, TaskActionsDropdownV2 } from 'components/pages/task/details/task-content-v2/actions/TaskActionsDropdownV2'
import { EscalateToEmergencyAccessButton } from 'components/pages/task/details/task-content-v2/escalate-to-emergency-access/EscalateToEmergencyAccessButton'
import { TaskValue } from 'lib/actions/task'
import { DisplayModeContext } from 'lib/context/app'
import { ClockPlus } from 'lucide-react'
import { FC, useContext, useMemo } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'

type TaskTitleProps = {
    task: TaskValue
    stepUpTxn?: string
    hasPendingActions?: boolean
}

const getDropdownOptions = (
    task: TaskValue,
    hasPendingActions: boolean,
): TaskActionOptions[] => {
    // Use pending actions if feature enabled + certify task + has access review
    const usePendingActions = hasPendingActions &&
        task.type?.taskType.oneofKind === 'certify' &&
        !!task.accessReview?.id

    const baseOptions: TaskActionOptions[] = [
        TaskActionOptions.comment,
        TaskActionOptions.reassign,
        TaskActionOptions.cancel,
        TaskActionOptions.delete,
        TaskActionOptions.restart,
        TaskActionOptions.hardReset,
        TaskActionOptions.sendReminder,
        TaskActionOptions.markProvisionComplete,
        TaskActionOptions.markProvisionErrored,
        TaskActionOptions.processNow,
        TaskActionOptions.skipStep,
    ]

    if (usePendingActions) {
        return [
            TaskActionOptions.pendingApproveWithComment,
            TaskActionOptions.pendingDenyWithComment,
            ...baseOptions,
        ]
    }

    return [
        TaskActionOptions.approveWithComment,
        TaskActionOptions.denyWithComment,
        ...baseOptions,
    ]
}

export const TaskTitle: FC<TaskTitleProps> = ({ task, hasPendingActions = false }) => {
    const typ = task?.type?.taskType
    const intl = useIntl()
    const displayModeContext = useContext(DisplayModeContext)

    const dropdownOptions = useMemo(
        () => getDropdownOptions(task, hasPendingActions),
        [task, hasPendingActions],
    )

    let outcomeTime
    switch (typ?.oneofKind) {
        case 'certify':
            outcomeTime = typ.certify.outcomeTime
            break
        case 'grant':
            outcomeTime = typ.grant.outcomeTime
            break
        case 'revoke':
            outcomeTime = typ.revoke.outcomeTime
            break
        case 'action':
            outcomeTime = typ.action.outcomeTime
            break
        default:
            return null
    }

    const isExtensionGrantTask = task.type?.taskType.oneofKind === 'grant' && !!task.type?.taskType.grant.source?.isExtension

    return <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
                <Typography variant="h3" component="div" mb={2}>
                    {displayModeContext === 'default' && <C1Title title={getTaskTitleIntl(task, intl)} />}
                    <TaskTitleFormattedMessage task={task} />
                </Typography>
            </Box>
            <Stack direction="row" alignItems="center" spacing={1}>
                <EscalateToEmergencyAccessButton task={task} sx={{ whiteSpace: 'nowrap' }} />
                <Box data-testid="task-actions-dropdown">
                    <TaskActionsDropdownV2
                        task={task}
                        options={dropdownOptions}
                    />
                </Box>
            </Stack>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={3}>
            {task.type?.taskType.oneofKind === 'grant' && task.emergencyAccess && <EmergencyAccessTaskIcon variant="contained" />}
            {isExtensionGrantTask && <C1Icon icon={ClockPlus} sx={{ color: 'warning.dark', fontSize: 18 }} />}
            <TaskStatusLabel task={task} />

            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="caption">
                    <FormattedMessage id="components_task_dialog_created_at" defaultMessage="Created" />
                </Typography>
                <RelativeTimestamp typographyVariant="caption" date={task.createdAt} variant="relative_date_only" showAbsoluteDateAfterDays={1} />
            </Stack>

            {outcomeTime && task.stepApproverIds.length === 0 && <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="caption">
                    <FormattedMessage id="components_task_dialog_created_at" defaultMessage="Resolved" />
                </Typography>
                <RelativeTimestamp typographyVariant="caption" date={outcomeTime} variant="relative_date_only" showAbsoluteDateAfterDays={1} />
            </Stack>}
        </Stack>
    </Box>
}
